import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import MuiTableContainer from '@mui/material/TableContainer';
import MuiTableRow from '@mui/material/TableRow';
import Spin from 'components/Spin';
import TextField from 'components/form/TextInput';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import PropTypes from 'prop-types';
import React, {
  useState,
  useMemo,
  useImperativeHandle,
  useEffect,
  useRef,
} from 'react';
import EnhancedTableHead from './TableHead';
import TablePagination from './TablePagination';
import TableRow from './TableRow';
import TextFilter from './TextFilter';

export { default as TableHeader } from './TableHeader';
export { default as TableActions } from './TableActions';
export { default as TableContainer } from './TableContainer';
import TableActions from './TableActions';
import TableContainer from './TableContainer';

const searchData = (data, columns, text) => {
  if (!text) return data;
  const trimmedSearchText = text.trim();
  if (!trimmedSearchText) return data;
  return data.filter((row) => {
    return columns
      .filter((columnDef) => {
        return columnDef.searchable === undefined ? true : columnDef.searchable;
      })
      .some((columnDef) => {
        if (columnDef.customFilterAndSearch) {
          return !!columnDef.customFilterAndSearch(
            trimmedSearchText,
            row,
            columnDef,
          );
        } else if (columnDef.dataField) {
          const value = get(row, columnDef.dataField);
          if (value) {
            return value
              .toString()
              .toUpperCase()
              .includes(trimmedSearchText.toUpperCase());
          }
        }
      });
  });
};

const DataTable = React.forwardRef(
  (
    {
      remote,
      data,
      columns,
      defaultSorted,
      selectRow,
      rowEvents,
      keyField,
      onTableChange,
      pagination,
      search,
      size = 'small',
      rowStyle,
      hover = false,
      loading,
      emptyMessage,
      noHeader,
      renderChild,
      bordered,
    },
    ref,
  ) => {
    const [sort, setSort] = useState({
      sortField: defaultSorted?.sortField,
      sortOrder: defaultSorted?.sortOrder,
    });
    const [page, setPage] = React.useState(pagination?.page ?? 0);
    const [rowsPerPage, setRowsPerPage] = React.useState(
      pagination?.rowsPerPage || 10,
    );
    const [query, setQuery] = useState('');
    const [currentFilters, setFilters] = useState({});
    const filtersRef = useRef(currentFilters);

    useImperativeHandle(ref, () => ({
      resetFilters,
      setQuery,
    }));

    useEffect(() => {
      if (pagination?.page !== undefined) {
        setPage(pagination.page);
      }
    }, [pagination?.page]);

    useEffect(() => {
      setPage(0);
    }, [query]);

    useEffect(() => {
      filtersRef.current = currentFilters;
    }, [currentFilters]);

    const onFilterChange = (field, value) => {
      const newFilters = { ...currentFilters, [field]: { filterVal: value } };
      if (!value) {
        delete newFilters[field];
      }
      setFilters(newFilters);
    };

    const onApplyFilters = () => {
      onTableChange('filter', { filters: filtersRef.current });
    };

    const resetFilters = () => {
      setFilters({});
      onTableChange('filter', { filters: {} });
    };

    const isSelected = (row) =>
      selectRow.selected.indexOf(row[keyField]) !== -1;

    const handleRequestSort = (property) => {
      const newSort = { sortField: property };
      if (sort.sortField === property) {
        newSort.sortOrder = sort.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        newSort.sortOrder = 'asc';
      }
      setSort(newSort);
      onTableChange('sort', newSort);
    };

    const handleClick = (e, row) => {
      rowEvents?.onClick(e, row);
    };

    const handleCheckBox = (row) => (e) => {
      selectRow.onSelect(row, e.target.checked);
    };

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
      onTableChange &&
        onTableChange('pagination', { page: newPage, limit: rowsPerPage });
    };

    const handleChangeRowsPerPage = (event) => {
      const limit = parseInt(event.target.value, 10);
      setRowsPerPage(limit);
      setPage(0);
      onTableChange && onTableChange('pagination', { limit, page: 0 });
    };

    const filteredData = useMemo(() => {
      if (data) {
        if (pagination?.page === undefined) {
          setPage(0);
        }
        return searchData(data, columns, query);
      }
      return [];
    }, [query, data]);

    const slicedRows = remote
      ? data
      : pagination
        ? filteredData.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
          )
        : filteredData;

    const selectionEnabled = selectRow?.onSelect;
    const pageCount = pagination?.total ?? filteredData.length;

    const rowBorderStyle = {
      '& .MuiTableRow-root:last-child td, .MuiTableRow-root:last-child th': {
        border: 0,
      },
    };

    const Container = bordered ? TableContainer : Box;

    return (
      <Container sx={{ width: '100%' }}>
        {search && (
          <TableActions>
            <TextField
              size="small"
              margin="none"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              startAdornment={<SearchIcon />}
              placeholder="Search..."
            />
          </TableActions>
        )}
        <Spin spinning={loading}>
          <MuiTableContainer>
            <Table size={size}>
              {!noHeader && (
                <EnhancedTableHead
                  size={size}
                  selectionEnabled={selectionEnabled}
                  rows={filteredData}
                  numSelected={selectRow?.selected.length}
                  columns={columns}
                  sortColumn={sort.sortField}
                  sortOrder={sort.sortOrder}
                  onSelectAllClick={selectRow?.onSelectAll}
                  onRequestSort={handleRequestSort}
                  filters={currentFilters}
                  onFilterChange={onFilterChange}
                  onApplyFilters={onApplyFilters}
                />
              )}
              <TableBody sx={{ ...(pagination ? {} : rowBorderStyle) }}>
                {slicedRows?.map((row, index) => {
                  const isItemSelected = selectRow ? isSelected(row) : false;

                  return (
                    <TableRow
                      index={index}
                      hover={hover}
                      key={keyField ? get(row, keyField) : index}
                      row={row}
                      keyField={keyField}
                      renderChildren={renderChild}
                      handleClick={
                        rowEvents?.onClick
                          ? (event) => handleClick(event, row)
                          : undefined
                      }
                      tabIndex={-1}
                      selected={isItemSelected}
                      style={isFunction(rowStyle) ? rowStyle(row) : rowStyle}
                      columns={columns}
                      selectable={!!selectRow}
                      isItemSelected={isItemSelected}
                      handleCheckBox={handleCheckBox(row)}
                    />
                  );
                })}
                {!loading && !slicedRows?.length ? (
                  <MuiTableRow>
                    <TableCell
                      colSpan={columns.length + (selectionEnabled ? 1 : 0)}
                    >
                      <Box
                        p={1}
                        sx={{
                          textAlign: 'center',
                        }}
                      >
                        {emptyMessage || 'No data found'}
                      </Box>
                    </TableCell>
                  </MuiTableRow>
                ) : null}
              </TableBody>
            </Table>
          </MuiTableContainer>
        </Spin>
        {pagination && (
          <TablePagination
            pagination={pagination}
            page={pagination?.page ?? page}
            count={pageCount}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
      </Container>
    );
  },
);

DataTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataField: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      formatter: PropTypes.func,
      formatExtraData: PropTypes.any,
      align: PropTypes.oneOf(['left', 'right', 'center']),
      numeric: PropTypes.bool,
      sx: PropTypes.object,
    }),
  ).isRequired,
  defaultSorted: PropTypes.shape({
    sortField: PropTypes.string,
    sortOrder: PropTypes.string,
  }),
  selectRow: PropTypes.shape({
    onSelect: PropTypes.func,
    onSelectAll: PropTypes.func,
    selected: PropTypes.array,
  }),
  pagination: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      rowsPerPageOptions: PropTypes.array,
      rowsPerPage: PropTypes.number,
      page: PropTypes.number,
    }),
  ]),
  keyField: PropTypes.string,
  search: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'large']),
  rowEvents: PropTypes.shape({
    onClick: PropTypes.func,
  }),
  onTableChange: PropTypes.func,
  rowStyle: PropTypes.func,
  emptyMessage: PropTypes.string,
};

export const textFilter = (props = {}) => ({
  Component: TextFilter,
  props,
});

export default DataTable;
